import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirebaseAuth, isFirebaseConfigured } from '../config/firebase';
import {
  getStoredToken,
  getStoredUser,
  loginWithGoogle,
  logout as logoutUser,
} from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(isFirebaseConfigured());
  const [error, setError] = useState('');

  useEffect(() => {
    const auth = getFirebaseAuth();

    if (!auth) {
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && getStoredToken() && getStoredUser()) {
        setUser(getStoredUser());
        setToken(getStoredToken());
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await loginWithGoogle();
      setUser(response.user);
      setToken(response.token);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await logoutUser();
    setUser(null);
    setToken(null);
    setError('');
  };

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      error,
      isAuthenticated: Boolean(token && user),
      isFirebaseConfigured: isFirebaseConfigured(),
      login,
      logout,
      setError,
    }),
    [user, token, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
